<template>
  <div class="app-container">
    <Container
      :title="t('route.member')"
      :hasLine="true"
    >
      <template #header>
        <SearchForm
          :formModel="searchFormModel"
          :formStruct="searchFormStruct"
          :submitButton="t('common.search')"
          :resetButton="t('common.reset')"
          @submit="handleFilter"
          @reset="resetForm"
        />
      </template>
      <template #main>
        <el-row>
          <el-col>
            <el-button
              v-if="$hasPermission(['SYS_ROLE_W'])"
              type="primary"
              size="large"
              style="margin-bottom: 10px"
              @click="handleCreate"
            >
              {{ t('common.append') }}
            </el-button>
          </el-col>
        </el-row>
        <el-table
          v-loading="listLoading"
          :data="list"
          border
          fit
          highlight-current-row
          style="width: 100%"
        >
          <el-table-column
            :label="t('table.id')"
            align="center"
            prop="roleId"
            width="100"
          />
          <el-table-column
            :label="t('dommonData.name')"
            prop="roleName"
            align="center"
          />
          <el-table-column
            :label="t('dommonData.isActive')"
            align="center"
            prop="active"
          >
            <template #default="{row}">
              <span>{{ row.active ? t('dommonData.active') : t('dommonData.notActive') }}</span>
            </template>
          </el-table-column>
          <el-table-column
            v-if="$hasPermission(['SYS_ROLE_W'])"
            :label="t('table.actions')"
            align="center"
            width="300"
            class-name="fixed-width"
          >
            <template #default="{row}">
              <el-button
                type="primary"
                size="mini"
                @click="handleUpdate(row)"
                style="min-width: 50px;"
              >
                {{ t("table.edit") }}
              </el-button>
              <el-button
                size="mini"
                type="primary"
                @click="handleAuthorization(row)"
                style="min-width: 50px;"
              >
                {{ t("role.editAuthorization") }}
              </el-button>
              <!--<el-button
                size="mini"
                type="danger"
                @click="handleDelete(row.roleId)"
                style="min-width: 50px;"
              >
                {{ t("table.delete") }}
              </el-button>-->
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          :total="total"
          v-show="total > 0"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :page-size="limit"
          :current-page="page"
          :page-sizes="[10, 20, 30, 50]"
          layout="total, sizes, prev, pager, next, jumper"
          style="text-align: right;"
        />
      </template>
    </Container>

    <el-dialog
      v-if="$hasPermission(['SYS_ROLE_W'])"
      :title="t(textMap[dialogStatus])"
      v-model="dialogFormVisible"
    >
      <el-form
        ref="dataForm"
        :rules="rules"
        :model="tempRoleModel"
        label-position="left"
        label-width="100px"
        style="width: 400px; margin-left: 50px"
      >
        <el-form-item prop="roleId">
          <el-input
            v-if="dialogStatus !== 'create'"
            v-show="false"
            v-model="tempRoleModel.roleId"
          />
        </el-form-item>

        <el-form-item
          :label="t('dommonData.name')"
          prop="roleName"
        >
          <el-input v-model="tempRoleModel.roleName" />
        </el-form-item>

        <el-form-item
          :label="t('dommonData.isActive')"
          prop="active"
        >
          <el-radio-group
            type="boolean"
            v-model="tempRoleModel.active"
          >
            <el-radio :label="true">
              {{ t("dommonData.active") }}
            </el-radio>
            <el-radio :label="false">
              {{ t("dommonData.notActive") }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <div
        class="dialog-footer"
        style="text-align: right;"
      >
        <el-button @click="dialogFormVisible = false">
          {{ t("table.cancel") }}
        </el-button>
        <el-button
          type="primary"
          @click="dialogStatus === 'create' ? createData() : updateData()"
        >
          {{ t("table.confirm") }}
        </el-button>
      </div>
    </el-dialog>

    <el-dialog
      v-if="$hasPermission(['SYS_ROLE_W'])"
      :title="t('role.editAuthorization')"
      v-model="dialogAuthorizationFormVisible"
    >
      <div style="text-align: right">
        <FilterHeader
          :filterData="filterPermission"
          :hasCreate="false"
          @search="getAllPermissions"
        />
      </div>
      <div class="editor-container">
        <DraggableList
          :list1="rolePermissions"
          :list2="allPermissions"
          :list1-title="t('role.permissions')"
          :list2-title="t('role.allPermissions')"
        />
      </div>
      <div
        class="dialog-footer"
        style="text-align: right;"
      >
        <el-button @click="dialogAuthorizationFormVisible = false">
          {{ t("table.cancel") }}
        </el-button>
        <el-button
          type="primary"
          @click="updateRoleAuthorization()"
        >
          {{ t("table.confirm") }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script lang='ts'>
import {
  defineComponent,
  reactive,
  toRefs,
  ref,
  nextTick,
  onMounted,
  unref
} from 'vue'
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { createModel } from '@/utils/component'
import Container from '@/components/form/container/Container.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import {
  getRoleListForPage,
  addRole,
  editRole,
  deleteRole,
  getRolePermission,
  roleAuthorization
} from '@/apis/roles'
import { allPermissionList } from '@/apis/permission'
import { RoleModel, RolePageParamModel, RoleCreateModel, RolePermissionViewModel, RolePermissionCreateModel } from '@/model/roleModel'
import { PermissionListParamModel } from '@/model/permission'
import FilterHeader from '@/components/filter_header/Index.vue'
import DraggableList from '@/components/draggable-permission/Index.vue'
import { useI18n } from 'vue-i18n'
export default defineComponent({
  name: 'Role',
  components: {
    SearchForm,
    Container,
    FilterHeader,
    DraggableList
  },
  setup() {
    const { t } = useI18n()

    const dataForm = ref(ElForm)
    const searchStruct = [
      { prop: 'roleName', label: t('role.name'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'active',
        label: t('dommonData.isActive'),
        component: 'el-select',
        model: true,
        options: [
          { label: t('dommonData.active'), value: true },
          { label: t('dommonData.notActive'), value: false }
        ]
      }
    ]
    const tempRoleModel: RoleModel = {
      roleId: null,
      roleName: null,
      active: true
    }
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as any,
      tableKey: 0,
      list: Array<RoleModel>(),
      total: 0,
      listLoading: true,
      page: 1,
      limit: 20,
      filterPermission: [
        { key: 'permissionName', isSelect: false, model: '', placeholder: t('permission.name') }
      ],

      tempRoleModel: tempRoleModel,
      rolePermissions: Array<RolePermissionViewModel>(),
      allPermissions: Array<RolePermissionViewModel>(),
      dialogFormVisible: false,
      dialogAuthorizationFormVisible: false,
      dialogStatus: 'create',
      textMap: {
        update: 'role.edit',
        create: 'role.create'
      },
      handleCurrentChange(page?: any) {
        dataMap.getList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getList(null, val)
      },
      async getList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }

        dataMap.listLoading = true
        const params: RolePageParamModel = {
          roleName: dataMap.searchFormModel.roleName,
          active: dataMap.searchFormModel.active,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getRoleListForPage(params)
        dataMap.list = data?.data.list ?? []
        dataMap.total = data?.data.totalCount ?? 0

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      handleFilter() {
        dataMap.page = 1
        dataMap.getList()
      },
      handleCreate() {
        dataMap.dialogStatus = 'create'
        dataMap.dialogFormVisible = true
        nextTick(() => {
          (dataForm.value as typeof ElForm).resetFields()
          dataMap.tempRoleModel.active = true
        })
      },
      createData() {
        const form = unref(dataForm)
        form.validate(async(valid: any) => {
          if (valid) {
            const roleCreateData: RoleCreateModel = {
              roleName: dataMap.tempRoleModel.roleName,
              active: dataMap.tempRoleModel.active
            }
            const result = await addRole(roleCreateData)
            dataMap.dialogFormVisible = false
            dataMap.getList()
            if (result?.data && result?.data > 0) {
              ElMessage({
                message: t('role.createSuccessMessage'),
                type: 'success',
                duration: 2000
              })
            } else {
              ElMessage({
                message: result?.msg,
                type: 'error',
                duration: 2000
              })
            }
          }
        })
      },

      async handleUpdate(row: any) {
        dataMap.dialogStatus = 'update'
        dataMap.dialogFormVisible = true
        await nextTick(() => {
          (dataForm.value as typeof ElForm).resetFields()
        })
        dataMap.tempRoleModel.roleId = row.roleId
        dataMap.tempRoleModel.roleName = row.roleName
        dataMap.tempRoleModel.active = row.active
      },
      updateData() {
        const form = unref(dataForm)
        form.validate(async(valid: any) => {
          if (valid) {
            const roleUpdateData: RoleModel = {
              roleId: dataMap.tempRoleModel.roleId,
              roleName: dataMap.tempRoleModel.roleName,
              active: dataMap.tempRoleModel.active
            }
            const result = await editRole(roleUpdateData)
            dataMap.dialogFormVisible = false
            dataMap.getList()
            if (result?.data && result?.data > 0) {
              ElMessage({
                message: t('role.editSuccessMessage'),
                type: 'success',
                duration: 2000
              })
            } else {
              ElMessage({
                message: result?.msg,
                type: 'error',
                duration: 2000
              })
            }
          }
        })
      },
      handleDelete(roleId: number) {
        ElMessageBox.confirm(t('role.deleteTipMessage'), t('message.tip'), {
          confirmButtonText: t('message.confirm'),
          cancelButtonText: t('message.cancel'),
          type: 'warning'
        }).then(async() => {
          await dataMap.deleteData(roleId)
        }).catch(() => {
          ElMessage({
            type: 'info',
            message: t('role.cancelDeleteMessage'),
            duration: 2000
          })
        })
      },
      async deleteData(roleId: number) {
        const result = await deleteRole(roleId)
        dataMap.getList()
        if (result?.data && result?.data > 0) {
          ElMessage({
            message: t('role.deleteMessage'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.msg,
            type: 'error',
            duration: 2000
          })
        }
      },
      async handleAuthorization(row: any) {
        dataMap.dialogAuthorizationFormVisible = true
        dataMap.tempRoleModel.roleId = row.roleId
        dataMap.tempRoleModel.roleName = row.roleName
        dataMap.tempRoleModel.active = row.active
        await dataMap.getRolePermissions(row.roleId)
        await dataMap.getAllPermissions()
      },
      async getRolePermissions(roleId: number) {
        const res = await getRolePermission(roleId)
        if (res == null) {
          dataMap.rolePermissions = []
        } else {
          dataMap.rolePermissions = res.data
        }
      },
      async getAllPermissions() {
        dataMap.allPermissions = []
        const params: PermissionListParamModel = {
          permissionName: dataMap.filterPermission[0].model
        }
        const res = await allPermissionList(params)
        if (res == null) {
          dataMap.allPermissions = []
        } else {
          res.data.forEach(obj => {
            if (dataMap.rolePermissions.every((k: any) => obj.permissionId !== k.permissionId)) {
              dataMap.allPermissions.push(obj)
            }
          })
          // dataMap.allPermissions = res.data
        }
      },
      async updateRoleAuthorization() {
        const permissionIds = Array<number>()
        dataMap.rolePermissions.forEach(obj => {
          if (obj.permissionId) {
            permissionIds.push(obj.permissionId)
          }
        })
        const params: RolePermissionCreateModel = {
          permissionIds: permissionIds
        }
        if (dataMap.tempRoleModel.roleId == null) {
          ElMessage({
            message: t('role.noRoleId'),
            type: 'error',
            duration: 2000
          })
          return
        }
        const res = await roleAuthorization(dataMap.tempRoleModel.roleId, params)
        dataMap.dialogAuthorizationFormVisible = false
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('role.updateAuthorizationSuccessMessage'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const rules = {
      roleName: [
        { required: true, message: t('dommonData.name').concat(t('form.isRequired')), trigger: 'blur' }
      ]
    }
    onMounted(() => {
      dataMap.getList(null, 20)
    })
    return { t, ...toRefs(dataMap), dataForm, rules }
  }
})
</script>
